import { useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, ChevronDown, Download, Users, ArrowRightLeft, ExternalLink, FileText, GitMerge, X } from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  worked: { label: 'Worked', cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  no_show: { label: 'No show', cls: 'bg-red-500/15 text-red-500 border-red-500/30' },
  missed: { label: 'MISSED', cls: 'bg-red-600/20 text-red-500 border-red-600/40 font-bold' },
  in_progress: { label: 'In progress', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  upcoming: { label: 'Upcoming', cls: 'bg-muted text-muted-foreground border-border' },
};

const groupByEmployee = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = r.employee_id || r.employee_name;
    if (!map.has(key)) {
      map.set(key, { key, employee_id: r.employee_id, employee_name: r.employee_name, rows: [], sched: 0, worked: 0 });
    }
    const g = map.get(key);
    g.rows.push(r);
    g.sched += r.scheduled_hours || 0;
    g.worked += r.worked_hours || 0;
  });
  return [...map.values()].sort((a, b) => (a.employee_name || '').localeCompare(b.employee_name || ''));
};

export const ClockSiteTotals = ({ data, month, category, onOpenEmployee, onChanged, readOnly = false, linkBase = '/admin/clock-history' }) => {
  const [openSite, setOpenSite] = useState(null);
  const [openEmp, setOpenEmp] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [moving, setMoving] = useState(null);
  const [mergeSrc, setMergeSrc] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');
  const [merging, setMerging] = useState(false);

  const mergeSite = async () => {
    if (!mergeTarget) { toast.error('Choose the correct site to merge into'); return; }
    if (!window.confirm(`Merge "${mergeSrc}" into "${mergeTarget}"?\n\nALL shifts and clock records of "${mergeSrc}" (every month) will be permanently renamed to "${mergeTarget}" and the totals will combine. This cannot be undone.`)) return;
    setMerging(true);
    try {
      const r = await axios.post(`${API}/admin/clock/site-totals/merge`, {
        from_site: mergeSrc,
        to_site: mergeTarget,
      });
      toast.success(`${r.data.message} — ${r.data.shifts_renamed} shift(s) renamed`);
      setMergeSrc(null);
      setMergeTarget('');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const otherCategory = category === 'retail' ? 'event' : 'retail';

  const moveSite = async (site) => {
    const label = otherCategory === 'retail' ? 'Retail' : 'Event';
    if (!window.confirm(`Move ALL shifts of "${site}" (every month) to ${label}? They will appear in ${label} Clock History and the ${label} calendar.`)) return;
    setMoving(site);
    try {
      const r = await axios.post(`${API}/admin/clock/site-totals/recategorize`, {
        site,
        to_category: otherCategory,
      });
      toast.success(`Moved ${r.data.shifts_updated} shift(s) to ${label}`);
      onChanged?.();
    } catch {
      toast.error('Failed to move site');
    } finally {
      setMoving(null);
    }
  };

  const exportSite = async (site) => {
    setExporting(site);
    try {
      const r = await axios.get(`${API}/admin/clock/site-totals/export`, {
        params: { month, category, site },
        responseType: 'blob',
      });
      const safe = site.replace(/[^a-zA-Z0-9 _-]/g, '_').replace(/\s+/g, '_');
      saveAs(new Blob([r.data]), `${safe}_${month}.xlsx`);
      toast.success('Excel downloaded');
    } catch {
      toast.error('Failed to export site Excel');
    } finally {
      setExporting(null);
    }
  };

  const exportInvoice = async (site) => {
    setExporting(`inv-${site}`);
    try {
      const r = await axios.get(`${API}/admin/clock/site-totals/export-invoice`, {
        params: { month, category, site },
        responseType: 'blob',
      });
      const safe = site.replace(/[^a-zA-Z0-9 _-]/g, '_').replace(/\s+/g, '_');
      saveAs(new Blob([r.data]), `${safe}_invoice_${month}.xlsx`);
      toast.success('Invoice Excel downloaded');
    } catch {
      toast.error('Failed to export invoice Excel');
    } finally {
      setExporting(null);
    }
  };

  const exportHours = async (site) => {
    setExporting(`hrs-${site}`);
    try {
      const r = await axios.get(`${API}/admin/clock/site-totals/export-hours`, {
        params: { month, category, site },
        responseType: 'blob',
      });
      const safe = site.replace(/[^a-zA-Z0-9 _-]/g, '_').replace(/\s+/g, '_');
      saveAs(new Blob([r.data]), `${safe}_hours_${month}.xlsx`);
      toast.success('Hours Excel downloaded');
    } catch {
      toast.error('Failed to export hours Excel');
    } finally {
      setExporting(null);
    }
  };

  const exportAllSites = async () => {
    setExporting('all-sites');
    try {
      const r = await axios.get(`${API}/admin/clock/site-totals/export-all-sites`, {
        params: { month, category },
        responseType: 'blob',
      });
      saveAs(new Blob([r.data]), `all_sites_${category}_${month}.xlsx`);
      toast.success('All Sites Excel downloaded');
    } catch {
      toast.error('Failed to export All Sites Excel');
    } finally {
      setExporting(null);
    }
  };

  const exportDayPdf = async (employeeId, employeeName, date) => {
    setExporting(`pdf-${employeeId}-${date}`);
    try {
      const r = await axios.get(`${API}/admin/clock/day-report-pdf`, {
        params: { employee_id: employeeId, date },
        responseType: 'blob',
      });
      const safe = (employeeName || 'employee').replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_');
      saveAs(new Blob([r.data], { type: 'application/pdf' }), `${safe}_${date}_clock_report.pdf`);
      toast.success('Day PDF downloaded');
    } catch {
      toast.error('Failed to export day PDF');
    } finally {
      setExporting(null);
    }
  };

  const exportEmployeeExcel = async (siteName, employeeId, employeeName) => {
    setExporting(`emp-${siteName}-${employeeId}`);
    try {
      const r = await axios.get(`${API}/admin/clock/site-totals/export-employee`, {
        params: { month, category, site: siteName, employee_id: employeeId },
        responseType: 'blob',
      });
      const safe = (employeeName || 'employee').replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_');
      saveAs(new Blob([r.data]), `${safe}_${month}.xlsx`);
      toast.success('Employee Excel downloaded');
    } catch {
      toast.error('Failed to export employee Excel');
    } finally {
      setExporting(null);
    }
  };

  if (!data || data.sites.length === 0) {
    const other = data?.other_category_count || 0;
    const otherLabel = category === 'retail' ? 'Event Clock History' : 'Retail Clock History';
    const otherPath = category === 'retail' ? `${linkBase}/event` : `${linkBase}/retail`;
    return (
      <Card className="bg-card border-border rounded-sm" data-testid="site-totals-empty">
        <CardContent className="p-6 text-center text-sm text-muted-foreground space-y-2">
          {data?.error ? (
            <p className="text-red-500 font-mono text-xs break-all" data-testid="site-totals-error">
              Failed to compute site totals: {data.error}
            </p>
          ) : (
            <p>No {category} shifts scheduled this month.</p>
          )}
          {other > 0 && (
            <p data-testid="site-totals-other-hint">
              {other} shift(s) for this month are categorised under{' '}
              <a href={otherPath} className="text-primary underline underline-offset-2">{otherLabel}</a>.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="site-totals-section">
      {!readOnly && (
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          disabled={exporting === 'all-sites'}
          onClick={exportAllSites}
          className="h-7 rounded-sm text-[11px] uppercase border-primary/50 text-primary hover:bg-primary/10"
          data-testid="all-sites-export-btn"
        >
          <Download className="w-3 h-3 mr-1" />
          {exporting === 'all-sites' ? 'Exporting…' : 'All Sites Excel'}
        </Button>
      </div>
      )}
      {data.sites.map((s) => {
        const open = openSite === s.site;
        return (
          <Card key={s.site} className="bg-card border-border rounded-sm overflow-hidden">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setOpenSite(open ? null : s.site)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenSite(open ? null : s.site); } }}
              className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-left hover:bg-muted/40 transition-colors cursor-pointer"
              data-testid={`site-row-${s.site.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <MapPin className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} />
                <span className="font-medium text-sm truncate">{s.site}</span>
                <span className="text-[11px] text-muted-foreground flex items-center gap-1 shrink-0">
                  <Users className="w-3 h-3" /> {s.employee_count}
                </span>
              </span>
              <span className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-muted-foreground">
                  Scheduled <strong className="text-foreground">{s.total_scheduled}h</strong>
                </span>
                <span className="text-xs text-muted-foreground">
                  Worked <strong className="text-primary">{s.total_worked}h</strong>
                </span>
                {!readOnly && (
                <>
                <Button
                  size="sm"
                  variant="outline"
                  asChild={false}
                  disabled={exporting === s.site}
                  onClick={(e) => { e.stopPropagation(); exportSite(s.site); }}
                  className="h-7 rounded-sm text-[11px] uppercase"
                  data-testid={`site-export-${s.site.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <Download className="w-3 h-3 mr-1" />
                  {exporting === s.site ? '…' : 'Excel'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); exportInvoice(s.site); }}
                  className="h-7 rounded-sm text-[11px] uppercase border-primary/50 text-primary hover:bg-primary/10"
                  data-testid={`site-invoice-${s.site.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <Download className="w-3 h-3 mr-1" />
                  {exporting === `inv-${s.site}` ? '…' : 'Invoice'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); exportHours(s.site); }}
                  className="h-7 rounded-sm text-[11px] uppercase border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
                  data-testid={`site-hours-${s.site.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <Download className="w-3 h-3 mr-1" />
                  {exporting === `hrs-${s.site}` ? '…' : 'Hours'}
                </Button>
                {s.site !== 'No shift record (clock only)' && (
                  <>
                  <Button
                    size="sm"
                    variant="outline"
                    asChild={false}
                    disabled={moving === s.site}
                    onClick={(e) => { e.stopPropagation(); moveSite(s.site); }}
                    className="h-7 rounded-sm text-[11px] uppercase text-muted-foreground"
                    title={`Move all shifts of this site to ${otherCategory === 'retail' ? 'Retail' : 'Event'}`}
                    data-testid={`site-move-${s.site.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <ArrowRightLeft className="w-3 h-3 mr-1" />
                    {moving === s.site ? '…' : `To ${otherCategory === 'retail' ? 'Retail' : 'Event'}`}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    asChild={false}
                    onClick={(e) => { e.stopPropagation(); setMergeSrc(mergeSrc === s.site ? null : s.site); setMergeTarget(''); }}
                    className="h-7 rounded-sm text-[11px] uppercase text-purple-400 border-purple-500/40 hover:bg-purple-500/10"
                    title="Merge this (wrongly named) site into another site — renames all its shifts"
                    data-testid={`site-merge-${s.site.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <GitMerge className="w-3 h-3 mr-1" />
                    Merge
                  </Button>
                  </>
                )}
                </>
                )}
                <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
              </span>
            </div>
            {mergeSrc === s.site && !readOnly && (
              <div
                className="border-t border-purple-500/40 bg-purple-500/5 px-3 py-2 flex flex-wrap items-center gap-2"
                onClick={(e) => e.stopPropagation()}
                data-testid="merge-panel"
              >
                <span className="text-xs text-purple-400 uppercase tracking-wider shrink-0">Merge into:</span>
                <select
                  value={mergeTarget}
                  onChange={(e) => setMergeTarget(e.target.value)}
                  className="flex-1 min-w-[180px] h-8 bg-background border border-border rounded-sm px-2 text-sm"
                  data-testid="merge-target-select"
                >
                  <option value="">— choose the correct site —</option>
                  {(data?.sites || [])
                    .map((x) => x.site)
                    .filter((n) => n !== s.site && n !== 'No shift record (clock only)' && n !== 'Unassigned site')
                    .map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <Button
                  size="sm"
                  disabled={merging || !mergeTarget}
                  onClick={mergeSite}
                  className="h-8 rounded-sm text-[11px] uppercase bg-purple-600 hover:bg-purple-500 text-white"
                  data-testid="merge-confirm-btn"
                >
                  <GitMerge className="w-3 h-3 mr-1" />
                  {merging ? 'Merging…' : 'Merge'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setMergeSrc(null); setMergeTarget(''); }}
                  className="h-8 rounded-sm"
                  data-testid="merge-cancel-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            {open && (
              <div className="border-t border-border divide-y divide-border/60" data-testid={`site-rows-${s.site.replace(/\s+/g, '-').toLowerCase()}`}>
                {groupByEmployee(s.rows).map((g) => {
                  const empKey = `${s.site}|${g.key}`;
                  const empOpen = openEmp === empKey;
                  return (
                    <div key={g.key}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setOpenEmp(empOpen ? null : empKey)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenEmp(empOpen ? null : empKey); } }}
                        className="px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs hover:bg-muted/40 transition-colors cursor-pointer"
                        data-testid={`site-emp-group-${g.employee_id}`}
                      >
                        <span className="font-medium text-foreground min-w-[140px] flex items-center gap-1.5">
                          {g.employee_name}
                          {onOpenEmployee && g.employee_id && (
                            <ExternalLink
                              className="w-3 h-3 text-muted-foreground hover:text-primary shrink-0"
                              onClick={(e) => { e.stopPropagation(); onOpenEmployee(g.employee_id); }}
                              data-testid={`site-emp-open-${g.employee_id}`}
                            />
                          )}
                        </span>
                        <span className="text-muted-foreground">{g.rows.length} shift{g.rows.length !== 1 ? 's' : ''}</span>
                        <span className="ml-auto flex items-center gap-3">
                          <span className="text-muted-foreground">Sched {Math.round(g.sched * 100) / 100}h</span>
                          <span className="text-muted-foreground">Worked <strong className="text-primary">{Math.round(g.worked * 100) / 100}h</strong></span>
                          {!readOnly && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); exportEmployeeExcel(s.site, g.employee_id, g.employee_name); }}
                            className="inline-flex items-center gap-1 text-[10px] uppercase text-emerald-400 hover:text-emerald-300 border border-emerald-500/40 rounded-sm px-1.5 py-0.5"
                            data-testid={`emp-excel-${g.employee_id}`}
                          >
                            <Download className="w-3 h-3" />
                            {exporting === `emp-${s.site}-${g.employee_id}` ? '…' : 'Excel'}
                          </button>
                          )}
                          <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', empOpen && 'rotate-180')} />
                        </span>
                      </div>
                      {empOpen && (
                        <div className="bg-muted/20 divide-y divide-border/40" data-testid={`site-emp-days-${g.employee_id}`}>
                          {g.rows.map((r, i) => {
                            const b = STATUS_BADGE[r.status] || STATUS_BADGE.upcoming;
                            return (
                              <div key={`${r.date}-${i}`} className="pl-8 pr-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                <span className="text-muted-foreground font-mono">{r.date}</span>
                                <span className="text-muted-foreground">{r.start_time && r.end_time ? `${r.start_time}–${r.end_time}` : 'no times'}</span>
                                <span className="text-muted-foreground">
                                  In <strong className="text-foreground">{r.clock_in || '—'}</strong> · Out <strong className="text-foreground">{r.clock_out || '—'}</strong>
                                </span>
                                <span className="ml-auto flex items-center gap-3">
                                  <span className="text-muted-foreground">Sched {r.scheduled_hours}h</span>
                                  <span className="text-muted-foreground">Worked <strong className="text-primary">{r.worked_hours}h</strong></span>
                                  <Badge variant="outline" className={cn('rounded-sm text-[10px] uppercase', b.cls)}>{b.label}</Badge>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); exportDayPdf(r.employee_id, r.employee_name, r.date); }}
                                    className="inline-flex items-center gap-1 text-[10px] uppercase text-red-400 hover:text-red-300 border border-red-500/40 rounded-sm px-1.5 py-0.5"
                                    data-testid={`day-pdf-${r.employee_id}-${r.date}`}
                                  >
                                    <FileText className="w-3 h-3" />
                                    {exporting === `pdf-${r.employee_id}-${r.date}` ? '…' : 'PDF'}
                                  </button>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default ClockSiteTotals;
